import { CHUNK_SIZE } from "../sim/world/constants";
import { blockIndex, PaletteSubChunk } from "../sim/world/palette";
import type { ChunkPos } from "../sim/world/types";
import type { WorldgenRequest } from "./types";

export interface WorldgenWorkerClient {
  onmessage: ((event: MessageEvent<unknown>) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
  postMessage(message: WorldgenRequest): void;
  terminate(): void;
}

export type WorldgenWorkerFactory = (entry: URL) => WorldgenWorkerClient;

/**
 * Rejection reason once the loader has failed closed. Streaming layers must
 * treat it as unrecoverable: the pool is gone, so retrying would only requeue
 * against a stopped loader forever.
 */
export class PermanentWorldgenError extends Error {
  readonly permanent = true;
  constructor(message: string) {
    super(message);
    this.name = "PermanentWorldgenError";
  }
}

interface PendingLoad {
  readonly position: ChunkPos;
  readonly resolve: (chunk: PaletteSubChunk) => void;
  readonly reject: (reason: Error) => void;
}

interface WorkerSlot {
  worker: WorldgenWorkerClient;
  activeRequestId: number | null;
}

function defaultWorkerFactory(_entry: URL): WorldgenWorkerClient {
  // Keep the URL literal inside Worker construction so Vite recognizes and
  // emits the production worker chunk instead of leaving a source .ts URL.
  return new Worker(new URL("./worker.ts", import.meta.url), { type: "module", name: "worldgen" });
}

function chunkFromTransfer(blocksBuffer: ArrayBuffer, lightBuffer: ArrayBuffer): PaletteSubChunk {
  const blocks = new Uint16Array(blocksBuffer);
  const light = new Uint16Array(lightBuffer);
  if (blocks.length !== CHUNK_SIZE ** 3 || light.length !== CHUNK_SIZE ** 3) {
    throw new RangeError("worldgen worker returned a malformed sub-chunk");
  }
  const chunk = new PaletteSubChunk(blocks[0]);
  for (let y = 0; y < CHUNK_SIZE; y += 1) {
    for (let z = 0; z < CHUNK_SIZE; z += 1) {
      for (let x = 0; x < CHUNK_SIZE; x += 1) {
        const index = blockIndex(x, y, z);
        const block = blocks[index];
        const packedLight = light[index];
        if (block !== undefined && block !== blocks[0]) chunk.set(x, y, z, block);
        if (packedLight !== undefined && packedLight !== 0) chunk.setLight(x, y, z, packedLight);
      }
    }
  }
  return chunk;
}

/**
 * Production ChunkLoader-shaped adapter. Its default factory starts the actual
 * `worker.ts` module, while the factory seam keeps the adapter headless-testable.
 */
export class WorldgenChunkLoader {
  private readonly slots: WorkerSlot[];
  private readonly pending = new Map<number, PendingLoad>();
  private readonly queue: number[] = [];
  private nextRequestId = 1;
  private stopped = false;
  private dispatchScheduled = false;

  constructor(
    readonly seed: number,
    private readonly factory: WorldgenWorkerFactory = defaultWorkerFactory,
    size = 1,
  ) {
    if (!Number.isFinite(seed)) throw new RangeError("world seed must be finite");
    if (!Number.isInteger(size) || size < 1) throw new RangeError("worldgen worker pool size must be positive");
    this.slots = Array.from({ length: size }, () => this.createSlot());
  }

  load(position: ChunkPos): Promise<PaletteSubChunk> {
    if (this.stopped) return Promise.reject(new PermanentWorldgenError("worldgen chunk loader is stopped"));
    return new Promise((resolve, reject) => {
      const requestId = this.nextRequestId++;
      this.pending.set(requestId, { position: Object.freeze({ ...position }), resolve, reject });
      this.queue.push(requestId);
      this.dispatch();
    });
  }

  /** Cancels queued or active work when the streamer moves to another ring. */
  cancel(position: ChunkPos): void {
    for (const [requestId, pending] of this.pending) {
      if (pending.position.cx !== position.cx || pending.position.cy !== position.cy || pending.position.cz !== position.cz) continue;
      this.pending.delete(requestId);
      const queuedAt = this.queue.indexOf(requestId);
      if (queuedAt >= 0) this.queue.splice(queuedAt, 1);
      const slot = this.slots.find((candidate) => candidate.activeRequestId === requestId);
      if (slot) {
        this.detachAndTerminate(slot.worker);
        slot.activeRequestId = null;
        try {
          slot.worker = this.createWorker(slot);
        } catch (error) {
          this.stop(error instanceof Error ? error : new Error("failed to replace cancelled worldgen worker"));
        }
      }
      pending.reject(new Error("worldgen chunk request cancelled"));
    }
    this.scheduleDispatch();
  }

  terminate(): void {
    if (this.stopped) return;
    this.stop(new Error("worldgen chunk loader terminated"));
  }

  private createSlot(): WorkerSlot {
    const slot = { worker: undefined as unknown as WorldgenWorkerClient, activeRequestId: null };
    slot.worker = this.createWorker(slot);
    return slot;
  }

  private createWorker(slot: WorkerSlot): WorldgenWorkerClient {
    const worker = this.factory(new URL("./worker.ts", import.meta.url));
    worker.onmessage = (event): void => {
      if (slot.worker === worker) this.receive(slot, event.data);
    };
    worker.onerror = (event): void => {
      if (slot.worker !== worker || this.stopped) return;
      const requestId = slot.activeRequestId;
      slot.activeRequestId = null;
      this.detachAndTerminate(worker);
      if (requestId !== null) {
        const pending = this.pending.get(requestId);
        this.pending.delete(requestId);
        pending?.reject(new Error(event.message || "worldgen worker failed"));
      }
      try {
        slot.worker = this.createWorker(slot);
      } catch (error) {
        this.stop(error instanceof Error ? error : new Error("failed to replace worldgen worker"));
        return;
      }
      this.dispatch();
    };
    return worker;
  }

  private dispatch(): void {
    if (this.stopped) return;
    for (const slot of this.slots) {
      if (slot.activeRequestId !== null) continue;
      const requestId = this.queue.shift();
      if (requestId === undefined) return;
      const pending = this.pending.get(requestId);
      if (!pending) continue;
      slot.activeRequestId = requestId;
      try {
        slot.worker.postMessage({
          type: "generate",
          requestId,
          seed: this.seed,
          cx: pending.position.cx,
          cy: pending.position.cy,
          cz: pending.position.cz,
        });
      } catch (error) {
        slot.activeRequestId = null;
        this.pending.delete(requestId);
        pending.reject(error instanceof Error ? error : new Error("worldgen worker request failed"));
        this.scheduleDispatch();
      }
    }
  }

  private scheduleDispatch(): void {
    if (this.dispatchScheduled || this.stopped) return;
    this.dispatchScheduled = true;
    queueMicrotask(() => {
      this.dispatchScheduled = false;
      this.dispatch();
    });
  }

  private receive(slot: WorkerSlot, value: unknown): void {
    if (!isRecord(value) || !Number.isSafeInteger(value.requestId)) {
      this.stop(new Error("worldgen worker returned a response without a valid request id"));
      return;
    }
    const requestId = value.requestId as number;
    const pending = this.pending.get(requestId);
    if (!pending) {
      this.stop(new Error(`worldgen worker returned unknown request id ${requestId}`));
      return;
    }
    if (slot.activeRequestId !== requestId) {
      this.stop(new Error(`worldgen worker returned out-of-order request id ${requestId}`));
      return;
    }
    slot.activeRequestId = null;
    this.pending.delete(requestId);
    try {
      if (value.type !== "generated") throw new TypeError("worldgen worker returned an unexpected response type");
      const { position } = pending;
      if (value.cx !== position.cx || value.cy !== position.cy || value.cz !== position.cz) {
        throw new RangeError("worldgen worker returned coordinates for a different sub-chunk");
      }
      if (!(value.blocks instanceof ArrayBuffer) || !(value.light instanceof ArrayBuffer)) {
        throw new TypeError("worldgen worker response is missing transferable buffers");
      }
      pending.resolve(chunkFromTransfer(value.blocks, value.light));
    } catch (error) {
      pending.reject(error instanceof Error ? error : new Error("invalid worldgen worker response"));
    }
    this.dispatch();
  }

  private failAll(error: Error): void {
    for (const pending of this.pending.values()) pending.reject(error);
    this.pending.clear();
  }

  /** A protocol violation makes every worker result suspect, so fail closed. */
  private stop(error: Error): void {
    this.stopped = true;
    this.queue.length = 0;
    for (const slot of this.slots) {
      slot.activeRequestId = null;
      this.detachAndTerminate(slot.worker);
    }
    this.failAll(new PermanentWorldgenError(error.message));
  }

  private detachAndTerminate(worker: WorldgenWorkerClient): void {
    worker.onmessage = null;
    worker.onerror = null;
    worker.terminate();
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
